import { useRef, useState, useEffect } from "react";

import { useClassNames } from "@/utils/classNames";
import { Text } from "@/components/atoms/Text";

import styles from "./VerticalScrollList.module.scss";
import { VerticalScrollListProps } from "./types";

/**
 * VerticalScrollList component that renders a fixed heading with a scrollable list of items.
 * Features gradient overlays at the top and bottom when there are more items to scroll.
 *
 * @param heading - The heading text to display at the top
 * @param headingClassName - Optional className for the heading element
 * @param scrollContainerClassName - Optional className for the scroll container element (use to override --scroll-container-max-height)
 * @param className - Additional CSS class for styling
 * @param children - List item children
 * @param attrs - Additional HTML attributes for the div element
 * @returns A vertical scroll list component
 *
 * @example
 * ```tsx
 * <VerticalScrollList heading="Select an Option">
 *   <SingleSelect active={true}>Option 1</SingleSelect>
 *   <SingleSelect active={false}>Option 2</SingleSelect>
 *   <SingleSelect active={false}>Option 3</SingleSelect>
 * </VerticalScrollList>
 * ```
 *
 * @example
 * Override max-height using CSS custom property:
 * ```scss
 * .scroll-container {
 *   --scroll-container-max-height: 80vh;
 * }
 * ```
 * ```tsx
 * <VerticalScrollList
 *   heading="Subtitles"
 *   scrollContainerClassName={styles["scroll-container"]}
 * >
 *   <SingleSelect active={true}>English</SingleSelect>
 * </VerticalScrollList>
 * ```
 */
const VerticalScrollList = ({
  heading,
  headingClassName,
  scrollContainerClassName,
  className,
  children,
  ...attrs
}: VerticalScrollListProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showTopMask, setShowTopMask] = useState(false);
  const [showBottomMask, setShowBottomMask] = useState(false);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Show top mask if scrolled down from the top
    setShowTopMask(scrollTop > 0);

    // Show bottom mask if not scrolled to the bottom
    // Add a small threshold (1px) to account for rounding errors
    setShowBottomMask(scrollTop + clientHeight < scrollHeight - 1);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Initial check
    handleScroll();

    // Add scroll listener
    container.addEventListener("scroll", handleScroll);

    // Add resize observer to handle content changes
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [children]);

  const cn = useClassNames(styles, styles["vertical-scroll-list"], className);

  const headingCn = useClassNames(styles, styles["heading"], headingClassName);

  const scrollContainerCn = useClassNames(
    styles,
    styles["scroll-container"],
    showTopMask && showBottomMask && styles["mask-both"],
    showTopMask && !showBottomMask && styles["mask-top"],
    !showTopMask && showBottomMask && styles["mask-bottom"],
    scrollContainerClassName,
  );

  return (
    <div className={cn} {...attrs}>
      <div className={styles["header"]}>
        <Text variant="h3" className={headingCn}>
          {heading}
        </Text>
      </div>
      <div className={styles["scroll-container-wrapper"]}>
        <div ref={scrollContainerRef} className={scrollContainerCn}>
          {children}
        </div>
      </div>
    </div>
  );
};

export { VerticalScrollList };
